import { useCallback, useRef } from 'react';
import {
  cancelAnimation,
  useAnimatedReaction,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

/**
 * 방향 전환 후 이만큼 더 이동하면 헤더를 숨기거나 다시 보인다.
 *
 * 올림(보이기)을 내림(숨기기)보다 크게 둔 이유: 숨기는 건 늦으면 답답하지만, 다시 나타나는 건
 * 살짝 위로 튄 것만으로 발생하면 헤더가 깜박이는 것처럼 보인다. 그래서 **올릴 때만 더 긴
 * 지속 이동을 요구**한다.
 *
 * ⚠️ 이 임계값을 키우는 것과 `DIRECTION_CHANGE_MIN_DELTA` 를 방향별로 키우는 것은 다르다.
 * 후자는 조건에 못 미치는 이벤트를 **버리므로**, 올림 쪽만 키우면 프레임당 몇 px 씩 올라가는
 * 느린 상향 스크롤이 전부 버려져 누적이 안 되고 헤더가 영영 안 나타난다. 지속 거리 요구는
 * 반드시 이쪽(누적 경로)에서 조절한다.
 */
const SCROLL_DOWN_THRESHOLD = 15;
const SCROLL_UP_THRESHOLD = 150;

/**
 * 방향 전환으로 인정하는 **한 이벤트의** 최소 이동량. 손 떨림·바운스 같은 미세 흔들림이
 * 기준점을 리셋하는 것만 막는 용도라 방향에 대칭이어야 한다(위 주석 참고).
 */
const DIRECTION_CHANGE_MIN_DELTA = 3;

export const useHomeScroll = () => {
  const headerVisible = useSharedValue(1);
  const scrollY = useSharedValue(0);

  // 방향 감지용 상태 (UI thread에서 유지)
  const isHidden = useSharedValue(0); // 0: visible, 1: hidden
  const lastScrollY = useSharedValue(0);
  const lastDirectionChangeY = useSharedValue(0);
  const lastDirection = useSharedValue(0); // 0: none, 1: up, -1: down

  useAnimatedReaction(
    () => scrollY.value,
    (currentY, prevY) => {
      if (prevY === null || prevY === undefined) return;

      // 최상단이면 항상 보이기
      if (currentY <= 0) {
        if (isHidden.value === 1) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.value = 0;
        }
        lastScrollY.value = 0;
        lastDirectionChangeY.value = 0;
        lastDirection.value = 0;
        return;
      }

      const delta = currentY - lastScrollY.value;

      // 미세한 흔들림 무시
      if (Math.abs(delta) < DIRECTION_CHANGE_MIN_DELTA) {
        lastScrollY.value = currentY;
        return;
      }
      const currentDirection = delta > 0 ? -1 : 1; // -1: down, 1: up

      lastScrollY.value = currentY;

      // 방향이 바뀌면 기준점 리셋
      if (
        lastDirection.value !== 0 &&
        currentDirection !== lastDirection.value
      ) {
        lastDirectionChangeY.value = currentY;
      }
      lastDirection.value = currentDirection;

      const distance = Math.abs(currentY - lastDirectionChangeY.value);
      const threshold =
        currentDirection === -1 ? SCROLL_DOWN_THRESHOLD : SCROLL_UP_THRESHOLD;

      if (distance > threshold) {
        if (currentDirection === -1 && isHidden.value === 0) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(0, { duration: 200 });
          isHidden.value = 1;
        } else if (currentDirection === 1 && isHidden.value === 1) {
          cancelAnimation(headerVisible);
          headerVisible.value = withTiming(1, { duration: 200 });
          isHidden.value = 0;
        }
      }
    },
  );

  // 웹 등에서 JS thread에서 scrollY를 주입해야 하는 경우를 위한 호환 콜백
  const onScrollY = useCallback(
    (y: number) => {
      scrollY.value = y;
    },
    [scrollY],
  );

  // 현재 활성 탭이 등록한 scrollToTop 함수만 보관.
  // 탭 변경 시 이전 탭이 unregister 하고 새 탭이 register 한다.
  const scrollToTopFnRef = useRef<(() => void) | null>(null);
  const registerScrollToTop = useCallback((fn: (() => void) | null) => {
    scrollToTopFnRef.current = fn;
  }, []);
  const scrollToTop = useCallback(() => {
    scrollToTopFnRef.current?.();
  }, []);

  return {
    headerVisible,
    scrollY,
    onScrollY,
    scrollToTop,
    registerScrollToTop,
  };
};
